// Simple placeholder pages for additional admin modules
import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { 
  appointmentsAPI, communicationsAPI, governanceAPI, 
  contributionsAPI, auditLogsAPI, reportsAPI, usersAPI, formatApiErrorDetail 
} from '../../lib/api';
import { Medal, Envelope, Scales, CurrencyDollar, ClockCounterClockwise, ChartBar, ShieldCheck, Plus } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

// Appointments Page
export const AppointmentsPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    appointmentsAPI.getAll().then(r => setAppointments(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="appointments-page">
        <div className="flex items-center gap-3">
          <Medal size={32} className="text-[#7B1818]" />
          <div>
            <h1 className="font-heading text-3xl text-[#1A1614]">Appointments & Ranks</h1>
            <p className="text-[#8A847A]">Rank history, promotions, and appointments</p>
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : appointments.length > 0 ? (
          <div className="bg-white border border-[#D8D2C4]">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left">Member</th>
                  <th className="px-4 py-3 text-left">Rank</th>
                  <th className="px-4 py-3 text-left">Office</th>
                  <th className="px-4 py-3 text-left">Effective Date</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map(a => (
                  <tr key={a.id}>
                    <td className="px-4 py-3">{a.member_id}</td>
                    <td className="px-4 py-3">{a.rank}</td>
                    <td className="px-4 py-3">{a.office || '-'}</td>
                    <td className="px-4 py-3">{a.effective_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white border border-[#D8D2C4] p-8 text-center text-[#8A847A]">
            No appointments recorded yet
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// Communications Page
export const CommunicationsPage = () => {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    communicationsAPI.getAll().then(r => setCommunications(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="communications-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Envelope size={32} className="text-[#7B1818]" />
            <div>
              <h1 className="font-heading text-3xl text-[#1A1614]">Communications</h1>
              <p className="text-[#8A847A]">Official communications and messages</p>
            </div>
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : communications.length > 0 ? (
          <div className="space-y-4">
            {communications.map(c => (
              <div key={c.id} className="bg-white border border-[#D8D2C4] p-6">
                <p className="text-overline text-xs mb-2">{c.recipient_type?.toUpperCase()}</p>
                <h3 className="font-heading text-lg text-[#1A1614]">{c.subject}</h3>
                <p className="text-[#4A453E] mt-2">{c.body}</p>
                <p className="text-xs text-[#8A847A] mt-4">{new Date(c.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-[#D8D2C4] p-8 text-center text-[#8A847A]">
            No communications sent yet
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// Governance Page
export const GovernancePage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    governanceAPI.getAll().then(r => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="governance-page">
        <div className="flex items-center gap-3">
          <Scales size={32} className="text-[#7B1818]" />
          <div>
            <h1 className="font-heading text-3xl text-[#1A1614]">Governance</h1>
            <p className="text-[#8A847A]">Meetings, resolutions, and minutes</p>
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : items.length > 0 ? (
          <div className="space-y-4">
            {items.map(item => (
              <div key={item.id} className="bg-white border border-[#D8D2C4] p-6">
                <p className="text-overline text-xs mb-2">{item.item_type?.toUpperCase()}</p>
                <h3 className="font-heading text-lg text-[#1A1614]">{item.title}</h3>
                {item.description && <p className="text-[#4A453E] mt-2">{item.description}</p>}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-[#D8D2C4] p-8 text-center text-[#8A847A]">
            No governance items recorded yet
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// Contributions Page
export const ContributionsPage = () => {
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ contributor_name: '', amount: '', purpose: '', date: '', notes: '' });

  const fetchData = () => {
    contributionsAPI.getAll().then(r => setContributions(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await contributionsAPI.create({ ...formData, amount: parseFloat(formData.amount) });
      toast.success('Contribution recorded');
      setShowModal(false);
      setFormData({ contributor_name: '', amount: '', purpose: '', date: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const total = contributions.reduce((sum, c) => sum + (c.amount || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="contributions-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CurrencyDollar size={32} className="text-[#7B1818]" />
            <div>
              <h1 className="font-heading text-3xl text-[#1A1614]">Contributions</h1>
              <p className="text-[#8A847A]">Total: ${total.toLocaleString()}</p>
            </div>
          </div>
          <Button onClick={() => setShowModal(true)} className="bg-[#7B1818] hover:bg-[#5C1212] rounded-none" data-testid="add-contribution-button">
            <Plus size={18} className="mr-2" /> Record Contribution
          </Button>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : contributions.length > 0 ? (
          <div className="bg-white border border-[#D8D2C4]">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left">Contributor</th>
                  <th className="px-4 py-3 text-left">Amount</th>
                  <th className="px-4 py-3 text-left">Purpose</th>
                  <th className="px-4 py-3 text-left">Date</th>
                </tr>
              </thead>
              <tbody>
                {contributions.map(c => (
                  <tr key={c.id}>
                    <td className="px-4 py-3">{c.contributor_name}</td>
                    <td className="px-4 py-3 font-medium">${c.amount?.toLocaleString()}</td>
                    <td className="px-4 py-3">{c.purpose}</td>
                    <td className="px-4 py-3">{c.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white border border-[#D8D2C4] p-8 text-center text-[#8A847A]">
            No contributions recorded yet
          </div>
        )}

        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Record Contribution</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contribution-form">
              <div>
                <label className="block text-sm font-medium mb-1">Contributor Name *</label>
                <Input value={formData.contributor_name} onChange={(e) => setFormData({...formData, contributor_name: e.target.value})} required className="rounded-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Amount (USD) *</label>
                  <Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} required className="rounded-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date *</label>
                  <Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} required className="rounded-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Purpose *</label>
                <Input value={formData.purpose} onChange={(e) => setFormData({...formData, purpose: e.target.value})} required className="rounded-none" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows={2} className="rounded-none" />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-none">Cancel</Button>
                <Button type="submit" className="bg-[#7B1818] hover:bg-[#5C1212] rounded-none">Record</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// Audit Logs Page
export const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    auditLogsAPI.getAll({ limit: 100 }).then(r => setLogs(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="audit-logs-page">
        <div className="flex items-center gap-3">
          <ClockCounterClockwise size={32} className="text-[#7B1818]" />
          <div>
            <h1 className="font-heading text-3xl text-[#1A1614]">Audit Logs</h1>
            <p className="text-[#8A847A]">System activity and changes</p>
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : logs.length > 0 ? (
          <div className="bg-white border border-[#D8D2C4]">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left">Action</th>
                  <th className="px-4 py-3 text-left">Entity</th>
                  <th className="px-4 py-3 text-left">User</th>
                  <th className="px-4 py-3 text-left">Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3 capitalize">{log.action?.replace('_', ' ')}</td>
                    <td className="px-4 py-3 capitalize">{log.entity_type?.replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-[#8A847A]">{log.user_id}</td>
                    <td className="px-4 py-3 text-[#8A847A]">{new Date(log.timestamp).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white border border-[#D8D2C4] p-8 text-center text-[#8A847A]">
            No audit logs recorded yet
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// Reports Page
export const ReportsPage = () => {
  const [membersByCommandery, setMembersByCommandery] = useState([]);
  const [contributionSummary, setContributionSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      reportsAPI.membersByCommandery(),
      reportsAPI.contributionSummary()
    ]).then(([mc, cs]) => {
      setMembersByCommandery(mc.data);
      setContributionSummary(cs.data);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="reports-page">
        <div className="flex items-center gap-3">
          <ChartBar size={32} className="text-[#7B1818]" />
          <div>
            <h1 className="font-heading text-3xl text-[#1A1614]">Reports</h1>
            <p className="text-[#8A847A]">Analytics and summaries</p>
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-[#D8D2C4] p-6">
              <h3 className="font-heading text-xl text-[#1A1614] mb-4">Members by Commandery</h3>
              {membersByCommandery.length > 0 ? (
                <div className="space-y-2">
                  {membersByCommandery.map((m, i) => (
                    <div key={i} className="flex justify-between py-2 border-b border-[#E8E4D9] last:border-0">
                      <span>{m.commandery}</span>
                      <span className="font-medium">{m.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#8A847A]">No data available</p>
              )}
            </div>
            <div className="bg-white border border-[#D8D2C4] p-6">
              <h3 className="font-heading text-xl text-[#1A1614] mb-4">Contributions by Purpose</h3>
              {contributionSummary.length > 0 ? (
                <div className="space-y-2">
                  {contributionSummary.map((c, i) => (
                    <div key={i} className="flex justify-between py-2 border-b border-[#E8E4D9] last:border-0">
                      <span>{c.purpose}</span>
                      <span className="font-medium">${c.total?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#8A847A]">No data available</p>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// Users & Roles Page
export const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const ROLES = ['super_admin', 'grand_prior', 'chancellor', 'registrar', 'treasurer', 'communications_officer', 'committee_officer', 'member', 'applicant'];

  const fetchData = () => {
    usersAPI.getAll().then(r => setUsers(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await usersAPI.updateRole(userId, newRole);
      toast.success('Role updated');
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="users-page">
        <div className="flex items-center gap-3">
          <ShieldCheck size={32} className="text-[#7B1818]" />
          <div>
            <h1 className="font-heading text-3xl text-[#1A1614]">Users & Roles</h1>
            <p className="text-[#8A847A]">Manage user permissions</p>
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : (
          <div className="bg-white border border-[#D8D2C4]">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Role</th>
                  <th className="px-4 py-3 text-left">Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td className="px-4 py-3 font-medium">{user.name}</td>
                    <td className="px-4 py-3 text-[#4A453E]">{user.email}</td>
                    <td className="px-4 py-3">
                      <Select value={user.role} onValueChange={(v) => handleRoleChange(user.id, v)}>
                        <SelectTrigger className="w-48 rounded-none h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map(r => <SelectItem key={r} value={r} className="capitalize">{r.replace('_', ' ')}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-[#8A847A]">{user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};
